import { useEffect, useState } from 'react';
import { fetchAllStaffVoices, type StaffVoiceItem } from '../../lib/wordpress';
import Header from '../home/components/Header';
import Footer from '../home/components/Footer';
import StaffVoiceCard from '../home/components/StaffVoiceCard';
import FloatingLineButton from '../home/components/FloatingLineButton';

const STAFF_CATEGORY_ID = 33;

export default function StaffVoicePage() {
  const [staffVoices, setStaffVoices] = useState<StaffVoiceItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://kaigo.trust-job.com';

  useEffect(() => {
    // SEO meta tags
    document.title = '派遣スタッフの声｜名古屋の介護派遣トラストで働くリアルな体験談';
    const setMeta = (name: string, content: string, prop = false) => {
      const attr = prop ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.content = content;
    };
    setMeta('description', '名古屋の介護派遣トラストで実際に働くスタッフの声をご紹介。ブランク復帰・週3日勤務・高時給など、リアルな体験談や収入例をお届けします。');
    setMeta('keywords', '介護派遣,スタッフの声,名古屋,トラスト,体験談,ブランク復帰');
    setMeta('og:title', '派遣スタッフの声｜名古屋の介護派遣トラストで働くリアルな体験談', true);
    setMeta('og:description', '名古屋の介護派遣トラストで実際に働くスタッフの声。ブランク復帰・週3日勤務・高時給など、リアルな体験談をお届けします。', true);
    setMeta('og:url', `${siteUrl}/staff-voice`, true);

    // Schema.org
    const schema = {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      '@id': `${siteUrl}/staff-voice`,
      url: `${siteUrl}/staff-voice`,
      name: '派遣スタッフの声｜名古屋の介護派遣トラスト',
      description: '名古屋の介護派遣トラストで実際に働くスタッフの声をご紹介。ブランク復帰・週3日勤務・高時給など、リアルな体験談や収入例をお届けします。',
      inLanguage: 'ja',
      isPartOf: { '@id': `${siteUrl}/#website` },
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'ホーム', item: siteUrl },
          { '@type': 'ListItem', position: 2, name: '派遣スタッフの声', item: `${siteUrl}/staff-voice` },
        ],
      },
    };

    document.querySelectorAll('script[data-schema="staff-voice"]').forEach((s) => s.remove());
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.dataset.schema = 'staff-voice';
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);

    return () => {
      document.querySelectorAll('script[data-schema="staff-voice"]').forEach((s) => s.remove());
    };
  }, [siteUrl]);

  useEffect(() => {
    const loadStaffVoices = async () => {
      try {
        setLoading(true);
        const data = await fetchAllStaffVoices(STAFF_CATEGORY_ID);
        setStaffVoices(data);
        setError(false);
      } catch (err) {
        console.error('派遣スタッフの声の取得に失敗:', err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    loadStaffVoices();
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-teal-50 via-white to-teal-50">
      <Header />

      <main className="flex-1 pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl mb-6 shadow-lg">
              <i className="ri-user-voice-line text-3xl text-white"></i>
            </div>
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              派遣スタッフの声
            </h1>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              実際に働いているスタッフの生の声をお届けします。<br />
              勤務スタイルや収入例、リアルな感想をぜひご覧ください。
            </p>
          </div>

          {loading && (
            <div className="flex justify-center items-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-teal-500 border-t-transparent"></div>
            </div>
          )}

          {error && !loading && (
            <div className="text-center py-20">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-4">
                <i className="ri-error-warning-line text-3xl text-red-500"></i>
              </div>
              <p className="text-gray-600 text-lg">
                データの読み込みに失敗しました。<br />
                しばらくしてから再度お試しください。
              </p>
            </div>
          )}

          {!loading && !error && staffVoices.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {staffVoices.map((staff) => (
                <StaffVoiceCard key={staff.id} staff={staff} />
              ))}
            </div>
          )}

          {!loading && !error && staffVoices.length === 0 && (
            <div className="text-center py-20">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-4">
                <i className="ri-user-line text-3xl text-gray-400"></i>
              </div>
              <p className="text-gray-600 text-lg">
                現在、表示できるスタッフの声がありません。
              </p>
            </div>
          )}
        </div>
      </main>

      <Footer />

      {/* 固定LINE相談ボタン */}
      <div className="fixed bottom-6 right-6 z-50">
        <div className="bg-[#06C755] rounded-full shadow-2xl px-5 py-3 flex items-center space-x-2 hover:bg-[#05b34c] transition-colors duration-200">
          <FloatingLineButton />
        </div>
      </div>
    </div>
  );
}